import React, { useRef, useState } from "react";
import Download from "../assets/svgIcons/download.svg";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { formatDate, formatFileUrlValue } from "../utils/Formatter";
import DownloadFormatModal from "./DownloadFormatModal";
import Logo from "../assets/icons/FINEPUNCHLOGO.png";
import "../styles/components/DownloadButton.css";
import { createPdfTablePageBreakRepair } from "../utils/pdfTablePageBreaks";

export const drawPageNumbers = (pdf) => {
  const totalPages = pdf.internal.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    pdf.setPage(pageNumber);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(0, 0, 0);
    pdf.text(
      `${pageNumber}/${totalPages}`,
      pdf.internal.pageSize.getWidth() / 2,
      pdf.internal.pageSize.getHeight() - 1.5,
      { align: "center" },
    );
  }
};

const waitForImages = async (rootEl, timeoutMs = 8000) => {
  const images = Array.from(rootEl.querySelectorAll("img"));
  if (images.length === 0) return;

  const withTimeout = (promise) =>
    Promise.race([
      promise,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);

  await withTimeout(
    Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            const onDone = () => {
              img.removeEventListener("load", onDone);
              img.removeEventListener("error", onDone);
              resolve();
            };
            img.addEventListener("load", onDone);
            img.addEventListener("error", onDone);
          }),
      ),
    ),
  );
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Redraws a 3-column meta row block (e.g. Prepared/Revised/Approved By) pinned
// to the bottom margin of the current jsPDF page, so it repeats on every page.
// The note (e.g. "system-generated document") is only drawn below the rows
// on the last page, right after the footer, never mid-page elsewhere.
const drawRepeatingFooter = (pdf, rows, margin, note, isLastPage) => {
  const marginLeft = margin[1];
  const marginBottom = margin[2];
  const marginRight = margin[3];
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const tableWidth = pageWidth - marginLeft - marginRight;
  const rowHeight = 8;
  const colWidth = tableWidth / 3;
  const noteHeight = isLastPage && note ? 6 : 0;
  let y = pageHeight - marginBottom - rowHeight * rows.length - noteHeight;

  pdf.setDrawColor(156, 163, 175);
  pdf.setLineWidth(0.2);

  rows.forEach((row) => {
    row.forEach((cell, colIndex) => {
      const x = marginLeft + colIndex * colWidth;
      pdf.rect(x, y, colWidth, rowHeight);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.text(cell.label, x + 2, y + 5);
      const labelWidth = pdf.getTextWidth(`${cell.label} `);
      pdf.setFont("helvetica", "normal");
      pdf.text(cell.value, x + 2 + labelWidth, y + 5);
    });
    y += rowHeight;
  });

  if (noteHeight) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    const noteWidth = pdf.getTextWidth(note);
    pdf.text(note, marginLeft + (tableWidth - noteWidth) / 2, y + 4);
  }
};

const drawRepeatingHeader = (
  pdf,
  headerImage,
  margin,
  headerHeight,
  headerOffset,
  headerWidth,
  borderColor = [156, 163, 175],
) => {
  const marginLeft = margin[1];

  pdf.addImage(
    headerImage,
    "PNG",
    marginLeft + headerOffset,
    margin[0],
    headerWidth,
    headerHeight,
  );
  pdf.setDrawColor(...borderColor);
  pdf.setLineWidth(0.2);
  // The captured header excludes the outer frame drawn by its parent, so redraw it.
  pdf.rect(marginLeft + headerOffset, margin[0], headerWidth, headerHeight);
};

const inlineImagesAsDataUrls = async (rootEl) => {
  const images = Array.from(rootEl.querySelectorAll("img"));
  if (images.length === 0) return;

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute("src") || "";
      if (!src || src.startsWith("data:") || src.startsWith("blob:")) return;
      img.setAttribute("crossorigin", "anonymous");

      try {
        const res = await fetch(src, {
          mode: "cors",
          cache: "no-cache",
          credentials: "include",
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await blobToDataUrl(blob);
        img.setAttribute("src", dataUrl);
      } catch {}
    }),
  );
};

const DownloadButton = ({
  tableData,
  tableHeadings,
  fileName: providedFileName = "data",
  downloadFormats = ["excel", "pdf"],
  fullPageDownload,
  primaryColor = "#333333",
  fullPageSelector = ".tab-content-detail-wrapper",
  refNo = "",
  revNo,
  revDate,
  fetchAllRows,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const rowsOverrideRef = useRef(null);
  const fileName = tableData?.downloadFileName || providedFileName;
  const pdfFileName = tableData?.downloadFileName
    ? `${fileName}.pdf`
    : `${fileName}_${new Date().toISOString().split("T")[0]}.pdf`;

  const getExportRows = () => rowsOverrideRef.current || tableData?.rows || [];

  const getRowValue = (row, fieldName) => {
    if (!row || !fieldName) {
      return undefined;
    }

    if (row[fieldName] !== undefined) {
      return row[fieldName];
    }

    const normalizeKey = (key) =>
      String(key || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const targetKey = normalizeKey(fieldName);
    const matchedKey = Object.keys(row).find(
      (key) => normalizeKey(key) === targetKey,
    );

    return matchedKey ? row[matchedKey] : undefined;
  };

  const isBooleanField = (heading, fieldName, rows) => {
    const normalizedDisplayName =
      heading?.display_name?.toLowerCase().replace(/[^a-z0-9]+/g, " ") || "";
    const normalizedFieldName =
      String(fieldName || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ") || "";

    const isSerialNumberField =
      /\b(sl|serial|sr)\s*no\b/.test(normalizedDisplayName) ||
      /^sl\b/.test(normalizedDisplayName) ||
      /\b(sl|serial|sr)\s*no\b/.test(normalizedFieldName) ||
      /^sl\b/.test(normalizedFieldName);
    const isRevisionField =
      /\b(revised|revision)\b/.test(normalizedDisplayName) ||
      /\b(revised|revision)\b/.test(normalizedFieldName);

    if (isSerialNumberField || isRevisionField) {
      return false;
    }

    if (heading) {
      const fieldType = heading.field_type?.toLowerCase();
      const dataType = heading.data_type?.toLowerCase();

      if (
        fieldType === "boolean" ||
        dataType === "boolean" ||
        fieldType === "checkbox"
      ) {
        return true;
      }
    }

    if (rows && rows.length > 0) {
      const sampleSize = Math.min(rows.length, 50);
      const missingTokens = new Set(["", "na", "n/a", "null", "-"]);
      const trueTokens = new Set(["1", "true", "yes", "y", "on", "checked"]);
      const falseTokens = new Set([
        "0",
        "false",
        "no",
        "n",
        "off",
        "unchecked",
      ]);
      let booleanLikeCount = 0;
      let nonBooleanCount = 0;

      for (let i = 0; i < sampleSize; i++) {
        const value = getRowValue(rows[i], fieldName);

        if (value === null || value === undefined) {
          continue;
        }

        const normalizedValue = String(value).trim().toLowerCase();
        if (missingTokens.has(normalizedValue)) {
          continue;
        }

        if (
          trueTokens.has(normalizedValue) ||
          falseTokens.has(normalizedValue)
        ) {
          booleanLikeCount++;
        } else {
          nonBooleanCount++;
        }
      }

      if (booleanLikeCount > 0 && nonBooleanCount === 0) {
        return true;
      }
    }

    return false;
  };

  const prepareDownloadData = () => {
    if (
      (!tableData?.rows || !tableData?.headings) &&
      downloadFormats.filter((f) => f !== "full-page-pdf").length > 0
    ) {
      console.error("Invalid table data structure");
      return null;
    }

    if (tableData?.rows && tableData?.headings) {
      const exportRows = getExportRows();
      const visibleHeadings = tableData.headings.filter(
        (heading) => !heading.is_internal,
      );

      const headers = visibleHeadings.map((heading) => heading.display_name);

      const orderedFields = visibleHeadings.map(
        (heading) => heading.field_name,
      );

      const booleanColumnIndices = orderedFields.map((fieldName, index) => {
        const heading = visibleHeadings[index];
        return isBooleanField(heading, fieldName, exportRows);
      });

      const data = exportRows.map((row) =>
        orderedFields.map((fieldName, index) => {
          let value = getRowValue(row, fieldName);
          const heading = visibleHeadings[index];
          const isBooleanFieldType = isBooleanField(
            heading,
            fieldName,
            exportRows,
          );

          const displayText = (heading?.display_name || "").toLowerCase();
          const looksLikeIsoDate =
            typeof value === "string" &&
            /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?/.test(value.trim());
          const isDateFieldType =
            (/date|_at$|_dt$/i.test(fieldName) &&
              !/date_range|date_type/i.test(fieldName)) ||
            /\bdate\b|\bdt\b/.test(displayText) ||
            looksLikeIsoDate;
          const isRevisedQuoteField = /\brevised\b/i.test(
            `${heading?.display_name || ""} ${fieldName}`,
          );
          const isRemarksField = /remark/i.test(
            `${heading?.display_name || ""} ${fieldName}`,
          );
          const isDrawingField = /drawing|dwg/i.test(
            `${heading?.display_name || ""} ${fieldName}`,
          );
          const isBlankRemark =
            isRemarksField &&
            (value === null ||
              value === undefined ||
              String(value).trim() === "" ||
              String(value).trim().toLowerCase() === "n/a");

          if (isBlankRemark) {
            value = "";
          } else if (
            isRevisedQuoteField &&
            row.revision_no !== undefined &&
            row.revision_no !== null
          ) {
            value = `R${Number(row.revision_no) || 0}`;
          } else if (isBooleanFieldType) {
            const normalizedBooleanValue = String(value ?? "")
              .trim()
              .toLowerCase();
            const booleanValue = ["1", "true", "yes", "y"].includes(
              normalizedBooleanValue,
            );
            value = booleanValue ? "__BOOLEAN_TRUE__" : "-";
          } else if (isDateFieldType) {
            value = formatDate(value);
          } else if (isDrawingField) {
            value = formatFileUrlValue(value);
          } else if (typeof value === "string") {
            value = value.trim();
            if (!value) {
              value = "N/A";
            } else if (value.length > 120 && !isRemarksField) {
              value = value.substring(0, 117) + "...";
            }
          } else if (typeof value === "number") {
            value = value.toString();
          } else if (value === null || value === undefined) {
            value = "N/A";
          } else {
            value = String(value);
          }

          return value;
        }),
      );

      return { headers, data, booleanColumnIndices };
    }

    return null;
  };

  const downloadExcel = () => {
    const downloadData = prepareDownloadData();
    if (!downloadData) {
      console.error("No data available for Excel download");
      return;
    }

    const { headers, data } = downloadData;

    const excelData = data.map((row) =>
      row.map((cell) => (cell === "__BOOLEAN_TRUE__" ? "?" : cell)),
    );
    const worksheetData = [headers, ...excelData];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const colWidths = headers.map(() => ({ wch: 20 }));
    worksheet["!cols"] = colWidths;

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);

        if (!worksheet[cell_ref]) continue;

        if (R === 0) {
          worksheet[cell_ref].s = {
            fill: { fgColor: { rgb: primaryColor.replace("#", "") } },
            font: { color: { rgb: "FFFFFF" }, bold: true },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
          };
        } else {
          worksheet[cell_ref].s = {
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
            alignment: { vertical: "center" },
          };
        }
      }
    }

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const downloadPDF = () => {
    const downloadData = prepareDownloadData();
    if (!downloadData) {
      console.error("No data available for PDF download");
      return;
    }

    const { headers, data, booleanColumnIndices } = downloadData;
    const doc = new jsPDF("landscape", "mm", "a4");

    const pageWidth = 297;
    const pageHeight = 210;
    const hasDocControl =
      Boolean(refNo) ||
      (revNo !== undefined && revNo !== null) ||
      Boolean(revDate);
    const headerHeight = hasDocControl ? 28 : 30;
    const headerTopMargin = 8;
    const headerY = headerTopMargin;

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16),
          ]
        : null;
    };

    const primaryRgb = hexToRgb(primaryColor) || [128, 128, 128];

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(10, headerY, pageWidth - 20, headerHeight);

    const section1Width = 90;
    const section2Width = pageWidth - 20 - section1Width - 90;
    const section3Width = 90;

    doc.line(
      10 + section1Width,
      headerY,
      10 + section1Width,
      headerY + headerHeight,
    );
    doc.line(
      pageWidth - 10 - section3Width,
      headerY,
      pageWidth - 10 - section3Width,
      headerY + headerHeight,
    );

    try {
      const logoSize = 18;
      const logoY = headerY + (headerHeight - logoSize) / 2 - 1;
      doc.addImage(Logo, "PNG", 12, logoY, logoSize, logoSize);
      doc.setFontSize(14);
      doc.setFont("times", "bold");
      doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
      const companyName = "FINE PUNCH FAB PVT LTD";
      const companyNameY = headerY + headerHeight / 2;
      doc.text(companyName, 32, companyNameY, {
        align: "left",
        baseline: "middle",
        maxWidth: section1Width - 22,
      });
      doc.setTextColor(0, 0, 0);
    } catch (error) {
      console.warn("Logo could not be loaded, using text only:", error);
      doc.setFontSize(14);
      doc.setFont("times", "bold");
      doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
      const companyName = "FINE PUNCH FAB PVT LTD";
      const companyNameY = headerY + headerHeight / 2;
      doc.text(companyName, 15, companyNameY, {
        align: "left",
        baseline: "middle",
        maxWidth: section1Width - 10,
      });
      doc.setTextColor(0, 0, 0);
    }

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    const title = fileName
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\s+DATA$/i, " REGISTER")
      .toUpperCase();
    const titleX = 10 + section1Width + section2Width / 2;

    const splitTextByWords = (text, maxWidth) => {
      const words = text.split(/\s+/);
      const lines = [];
      let currentLine = "";

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const wordWidth = doc.getTextWidth(word);

        if (wordWidth > maxWidth && word.includes("-")) {
          if (currentLine.trim()) {
            lines.push(currentLine.trim());
            currentLine = "";
          }
          const hyphenParts = word.split("-");
          let hyphenLine = "";

          for (let j = 0; j < hyphenParts.length; j++) {
            const part = hyphenParts[j];
            const partWithHyphen = j === 0 ? part : `-${part}`;
            const testHyphenLine = hyphenLine
              ? `${hyphenLine}${partWithHyphen}`
              : part;
            const testHyphenWidth = doc.getTextWidth(testHyphenLine);

            if (testHyphenWidth > maxWidth && hyphenLine) {
              lines.push(hyphenLine);
              hyphenLine = part;
            } else {
              hyphenLine = testHyphenLine;
            }
          }

          if (hyphenLine) {
            currentLine = hyphenLine;
          }
          continue;
        }

        if (wordWidth > maxWidth) {
          if (currentLine.trim()) {
            lines.push(currentLine.trim());
            currentLine = "";
          }
          const splitWord = doc.splitTextToSize(word, maxWidth);
          for (let j = 0; j < splitWord.length - 1; j++) {
            lines.push(splitWord[j]);
          }
          currentLine = splitWord[splitWord.length - 1];
          continue;
        }

        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = doc.getTextWidth(testLine);

        if (testWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine.trim()) {
            lines.push(currentLine.trim());
          }
          currentLine = word;
        }
      }

      if (currentLine.trim()) {
        lines.push(currentLine.trim());
      }

      return lines;
    };

    const maxTitleWidth = section2Width * 0.7;
    const splitTitle = splitTextByWords(title, maxTitleWidth);

    const lineHeight = 5;
    const totalTitleHeight = splitTitle.length * lineHeight;
    const titleY = headerY + (headerHeight - totalTitleHeight) / 2 + lineHeight;
    doc.text(splitTitle, titleX, titleY, { align: "center" });

    const metaFontSize = 9;
    const rightSectionCenterY = headerY + headerHeight / 2;
    const metaLineStep = 5;

    doc.setFontSize(metaFontSize);
    doc.setFont("helvetica", "normal");
    const rightSectionCenterX = pageWidth - 10 - section3Width / 2;

    const currentDate = new Date();
    const formattedDate = `${String(currentDate.getDate()).padStart(
      2,
      "0",
    )}-${String(currentDate.getMonth() + 1).padStart(
      2,
      "0",
    )}-${currentDate.getFullYear()}`;

    const revNoText =
      revNo === undefined || revNo === null
        ? ""
        : String(revNo).padStart(2, "0");
    const formatRevDate = (value) => {
      if (!value) return "";
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return "";
      return `${String(dt.getDate()).padStart(2, "0")}-${String(
        dt.getMonth() + 1,
      ).padStart(2, "0")}-${dt.getFullYear()}`;
    };
    const revDateText = formatRevDate(revDate);
    const refNoText = String(refNo || "")
      .replace(/^(?:\s*REF(?:ERENCE)?\s*\.?\s*NO\.?\s*:?\s*)+/i, "")
      .trim();
    const downloadedBy = (
      (localStorage.getItem("user_name") || "").trim() || "N/A"
    ).replace(/^./, (character) => character.toUpperCase());

    let finalPageCount = 1;

    const calculateColumnWidths = () => {
      const leftMargin = 10;
      const rightMargin = 10;
      const availableWidth = pageWidth - leftMargin - rightMargin;
      const numColumns = headers.length;
      const baseWidth = availableWidth / numColumns;
      const minWidth = 12;
      const maxWidth = 50;
      const columnWidths = [];
      let totalWidth = 0;

      for (let i = 0; i < numColumns; i++) {
        let width = baseWidth;
        const headerLength = headers[i]?.length || 0;
        const headerText = headers[i]?.toLowerCase() || "";

        if (
          headerText.includes("sl") ||
          headerText.includes("no") ||
          headerText === "sl. no"
        ) {
          width = Math.max(minWidth, baseWidth * 0.6);
        } else if (
          headerText.includes("customer") ||
          headerText.includes("company")
        ) {
          width = Math.min(maxWidth, baseWidth * 1.3);
        } else if (
          headerText.includes("description") ||
          headerText.includes("enq description") ||
          headerText.includes("doc. description")
        ) {
          width = Math.min(maxWidth, baseWidth * 1.5);
        } else if (headerText.includes("remark")) {
          width = Math.min(maxWidth, baseWidth * 1.2);
        } else if (
          headerText.includes("contact") ||
          headerText.includes("phone") ||
          headerText.includes("mail")
        ) {
          width = Math.min(maxWidth, baseWidth * 1.2);
        } else if (headerText.includes("date") || headerText.includes("time")) {
          width = Math.min(maxWidth, baseWidth * 1.0);
        } else if (headerLength > 20) {
          width = Math.min(maxWidth, baseWidth * 1.2);
        } else if (headerLength < 8) {
          width = Math.max(minWidth, baseWidth * 0.8);
        } else {
          width = Math.max(minWidth, Math.min(maxWidth, baseWidth));
        }

        columnWidths.push(width);
        totalWidth += width;
      }

      if (totalWidth > availableWidth) {
        const scaleFactor = availableWidth / totalWidth;
        const minShrinkWidth = 6;
        return columnWidths.map((width) => {
          const scaled = width * scaleFactor;
          return Math.max(minShrinkWidth, scaled);
        });
      } else if (totalWidth < availableWidth) {
        const scaleFactor = availableWidth / totalWidth;
        return columnWidths.map((width) => width * scaleFactor);
      }

      return columnWidths;
    };

    const columnWidths = calculateColumnWidths();
    const manyColumns = headers.length > 10;
    const baseFontSize = manyColumns ? 6 : 7;
    const baseCellPadding = manyColumns ? 1 : 2;
    const tableStartY = headerY + headerHeight + 5;
    const bottomMargin = 25;

    doc.autoTable({
      head: [headers],
      body: data,
      startY: tableStartY,
      styles: {
        fontSize: baseFontSize,
        cellPadding: baseCellPadding,
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        overflow: "linebreak",
        halign: "center",
        cellWidth: "wrap",
      },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        fontSize: baseFontSize,
        overflow: "linebreak",
        cellPadding: baseCellPadding,
      },
      bodyStyles: {
        valign: "middle",
        halign: "center",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontSize: baseFontSize,
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: columnWidths.reduce((styles, width, index) => {
        styles[index] = {
          cellWidth: width,
          halign: "center",
          valign: "middle",
          overflow: "linebreak",
        };
        return styles;
      }, {}),
      margin: { top: tableStartY, left: 10, right: 10, bottom: bottomMargin },
      pageBreak: "auto",
      rowPageBreak: "avoid",
      showHead: "everyPage",
      didParseCell: (hookData) => {
        if (
          hookData.section === "body" &&
          booleanColumnIndices[hookData.column.index]
        ) {
          const cellValue = hookData.cell.text[0];
          if (cellValue === "__BOOLEAN_TRUE__") {
            hookData.cell.text = [""];
            hookData.cell._drawTick = true;
          }
        }
      },
      didDrawCell: (hookData) => {
        if (hookData.section === "body" && hookData.cell._drawTick) {
          const cell = hookData.cell;
          const x = cell.x;
          const y = cell.y;
          const width = cell.width;
          const height = cell.height;
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          const tickSize = 2.5;
          const startX = centerX - tickSize * 0.5;
          const startY = centerY + tickSize * 0.1;
          const midX = centerX - tickSize * 0.1;
          const midY = centerY + tickSize * 0.5;
          const endX = centerX + tickSize * 0.5;
          const endY = centerY - tickSize * 0.3;

          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.8);
          doc.line(startX, startY, midX, midY);
          doc.line(midX, midY, endX, endY);
        }
      },
      didDrawPage: (data) => {
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        if (currentPage > 1) {
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.5);
          doc.rect(10, headerY, pageWidth - 20, headerHeight);

          doc.line(
            10 + section1Width,
            headerY,
            10 + section1Width,
            headerY + headerHeight,
          );
          doc.line(
            pageWidth - 10 - section3Width,
            headerY,
            pageWidth - 10 - section3Width,
            headerY + headerHeight,
          );

          try {
            const logoSize = 18;
            const logoY = headerY + (headerHeight - logoSize) / 2 - 1;
            doc.addImage(Logo, "PNG", 12, logoY, logoSize, logoSize);
            doc.setFontSize(14);
            doc.setFont("times", "bold");
            doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
            const companyName = "FINE PUNCH FAB PVT LTD";
            const companyNameY = headerY + headerHeight / 2;
            doc.text(companyName, 32, companyNameY, {
              align: "left",
              baseline: "middle",
              maxWidth: section1Width - 22,
            });
            doc.setTextColor(0, 0, 0);
          } catch (error) {
            console.warn("Logo could not be loaded, using text only:", error);
            doc.setFontSize(14);
            doc.setFont("times", "bold");
            doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
            const companyName = "FINE PUNCH FAB PVT LTD";
            const companyNameY = headerY + headerHeight / 2;
            doc.text(companyName, 15, companyNameY, {
              align: "left",
              baseline: "middle",
              maxWidth: section1Width - 10,
            });
            doc.setTextColor(0, 0, 0);
          }

          doc.setFontSize(15);
          doc.setFont("helvetica", "bold");
          const maxTitleWidth = section2Width * 0.7;
          const splitTitle = splitTextByWords(title, maxTitleWidth);
          const lineHeight = 5;
          const totalTitleHeight = splitTitle.length * lineHeight;
          const titleY =
            headerY + (headerHeight - totalTitleHeight) / 2 + lineHeight;
          doc.text(splitTitle, titleX, titleY, { align: "center" });

          doc.setFontSize(metaFontSize);
          doc.setFont("helvetica", "normal");
        }
      },
    });

    finalPageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= finalPageCount; i++) {
      doc.setPage(i);

      doc.setFontSize(metaFontSize);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      const metaLines = [];
      if (refNoText) metaLines.push({ label: "REF. NO", value: refNoText });
      if (revNoText) metaLines.push({ label: "REV. NO", value: revNoText });
      if (revDateText)
        metaLines.push({ label: "REV. DATE", value: revDateText });
      const metaStartY =
        rightSectionCenterY - ((metaLines.length - 1) * metaLineStep) / 2;
      const metaLabelX = pageWidth - 10 - section3Width + 10;
      const metaLabelWidth = Math.max(
        0,
        ...metaLines.map((line) => doc.getTextWidth(line.label)),
      );
      const metaColonX = metaLabelX + metaLabelWidth + 2;
      const metaValueX = metaColonX + 3;
      metaLines.forEach(({ label, value }, index) => {
        const y = metaStartY + index * metaLineStep;
        doc.text(label, metaLabelX, y, { baseline: "middle" });
        doc.text(":", metaColonX, y, { baseline: "middle" });
        doc.text(value, metaValueX, y, { baseline: "middle" });
      });
      doc.setFontSize(8);
      doc.setTextColor(100);
      const footerY = pageHeight - bottomMargin + 5;
      doc.text(
        `Download Date : ${formattedDate}    Download By : ${downloadedBy}`,
        pageWidth - 10,
        footerY,
        { align: "right", maxWidth: pageWidth - 45 },
      );
      doc.text(`Page ${i} of ${finalPageCount}`, 10, footerY);
    }

    doc.save(`${fileName}.pdf`);
  };

  const downloadECFullPagePDF = () => {
    const downloadData = prepareDownloadData();
    if (!downloadData) {
      console.error("No data available for EC full-page PDF download");
      return;
    }

    const { headers, data, booleanColumnIndices } = downloadData;
    const doc = new jsPDF("landscape", "mm", "a3");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    const topOffset = 10;
    const bottomMargin = 8;
    const availableTableWidth = pageWidth - margin * 2;
    const columnWidth = availableTableWidth / Math.max(headers.length, 1);

    const columnStyles = headers.reduce((styles, _header, index) => {
      styles[index] = {
        cellWidth: columnWidth,
        halign: "center",
        valign: "middle",
      };
      return styles;
    }, {});

    doc.autoTable({
      head: [headers],
      body: data,
      startY: topOffset,
      margin: {
        top: topOffset,
        left: margin,
        right: margin,
        bottom: bottomMargin,
      },
      tableWidth: availableTableWidth,
      styles: {
        fontSize: 5.8,
        cellPadding: 1,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        overflow: "linebreak",
        textColor: [0, 0, 0],
        fillColor: [255, 255, 255],
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles,
      pageBreak: "avoid",
      rowPageBreak: "avoid",
      didParseCell: (hookData) => {
        if (
          hookData.section === "body" &&
          booleanColumnIndices[hookData.column.index]
        ) {
          const cellValue = hookData.cell.text[0];
          if (cellValue === "__BOOLEAN_TRUE__") {
            hookData.cell.text = [""];
            hookData.cell._drawTick = true;
          }
        }
      },
      didDrawCell: (hookData) => {
        if (hookData.section === "body" && hookData.cell._drawTick) {
          const cell = hookData.cell;
          const x = cell.x;
          const y = cell.y;
          const width = cell.width;
          const height = cell.height;
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          const tickSize = 2;
          const startX = centerX - tickSize * 0.5;
          const startY = centerY + tickSize * 0.1;
          const midX = centerX - tickSize * 0.1;
          const midY = centerY + tickSize * 0.5;
          const endX = centerX + tickSize * 0.5;
          const endY = centerY - tickSize * 0.3;

          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.5);
          doc.line(startX, startY, midX, midY);
          doc.line(midX, midY, endX, endY);
        }
      },
    });

    const totalPages = doc.internal.getNumberOfPages();
    if (totalPages > 1) {
      console.warn(
        "EC full-page PDF exceeded one page; content density is high.",
      );
    }

    doc.setPage(1);
    doc.setFontSize(7);
    doc.setTextColor(80);
    doc.text(
      `Generated: ${new Date().toLocaleDateString("en-GB")}`,
      margin,
      pageHeight - 3.5,
    );

    doc.save(pdfFileName);
  };

  const downloadFullPage = async () => {
    let element = document.querySelector(fullPageSelector);

    if (!element) {
      const commonSelectors = [
        ".enquiry-page-container",
        ".invoice-page-container",
        ".service-invoice-page-container",
        ".tab-content-detail-wrapper",
      ];

      for (const selector of commonSelectors) {
        element = document.querySelector(selector);
        if (element) break;
      }
    }

    if (!element) {
      console.error(
        `Download element not found with selector: ${fullPageSelector}`,
      );
      return;
    }

    if (element.classList.contains("ec-invoice-page")) {
      setIsDownloading(true);
      try {
        downloadECFullPagePDF();
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    setIsDownloading(true);

    const clonedElement = element.cloneNode(true);
    clonedElement.classList.add("pdf-download");
    const buttonsToRemove = clonedElement.querySelectorAll(
      ".download-button, .invoice-buttons, .ec-invoice-download",
    );
    buttonsToRemove.forEach((button) => button.remove());
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.left = "-9999px";
    tempContainer.appendChild(clonedElement);
    document.body.appendChild(tempContainer);
    await waitForImages(clonedElement);
    await inlineImagesAsDataUrls(clonedElement);
    await waitForImages(clonedElement);

    const tableWidth = clonedElement.offsetWidth;
    const contentScrollWidth =
      clonedElement.scrollWidth || clonedElement.offsetWidth;
    const contentClientWidth =
      clonedElement.clientWidth || clonedElement.offsetWidth;
    const isWideContent =
      tableWidth > 800 || contentScrollWidth > contentClientWidth + 5;

    if (isWideContent) {
      // Ensure horizontally scrollable invoice tables are fully expanded before capture.
      const tableWraps = clonedElement.querySelectorAll(
        ".ec-invoice-table-wrap",
      );
      tableWraps.forEach((wrap) => {
        wrap.style.overflow = "visible";
        wrap.style.maxWidth = "none";
        wrap.style.width = "max-content";
      });

      const tables = clonedElement.querySelectorAll(".ec-invoice-table");
      tables.forEach((table) => {
        table.style.width = "max-content";
        table.style.maxWidth = "none";
      });
    }

    const isCustomerOrderLayout = clonedElement.classList.contains(
      "sales-customer-order-layout",
    );
    const isWorkOrderLayout =
      clonedElement.classList.contains("workorder-layout");
    const isMaterialRequestLayout = clonedElement.classList.contains(
      "mr-workorder-layout",
    );
    const omitWorkOrderFooter = isWorkOrderLayout && !isMaterialRequestLayout;
    if (omitWorkOrderFooter) {
      clonedElement
        .querySelectorAll(".invoice-footer-meta-block")
        .forEach((element) => element.remove());
    }
    const usesRepeatingFooter = isCustomerOrderLayout || isWorkOrderLayout;
    const isProformaLayout = clonedElement.classList.contains(
      "proforma-invoice-layout",
    );

    // Pull the Prepared/Revised/Approved and Ref/Revision meta rows out of the
    // normal document flow so they can be redrawn as a repeating footer on
    // every page of the exported PDF instead of only trailing the content once.
    let customerOrderFooterRows = null;
    let customerOrderFooterNote = "";
    if (usesRepeatingFooter && !omitWorkOrderFooter) {
      const footerBlocks = clonedElement.querySelectorAll(
        ".invoice-footer-meta-block",
      );
      customerOrderFooterRows = Array.from(footerBlocks).map((block) =>
        Array.from(block.querySelectorAll(".workorder-meta-cell")).map(
          (cell) => ({
            label:
              cell.querySelector(".workorder-meta-label")?.textContent || "",
            value:
              cell.querySelector(".workorder-meta-value")?.textContent || "",
          }),
        ),
      );
      footerBlocks.forEach((block) => {
        block.style.display = "none";
      });

      const noteEl = clonedElement.querySelector(
        ".invoice-system-generated-note",
      );
      if (noteEl) {
        customerOrderFooterNote = noteEl.textContent || "";
        // CSS forces this visible with !important on .pdf-download, so the
        // inline style must also carry !important to actually hide it.
        noteEl.style.setProperty("display", "none", "important");
      }
    }

    let repeatingHeaderImage = null;
    let repeatingHeaderReserve = 0;
    let repeatingHeaderOffset = 0;
    let repeatingHeaderWidth = 0;
    const estimatedFooterReserve = customerOrderFooterRows?.length
      ? customerOrderFooterRows.length * 8 +
        (customerOrderFooterNote ? 6 : 0) +
        4
      : 0;
    const workOrderPageContentHeight =
      clonedElement.getBoundingClientRect().width *
      ((297 - 15 - 10 - estimatedFooterReserve) / 190);
    const shouldRepeatWorkOrderHeader =
      isWorkOrderLayout &&
      !isMaterialRequestLayout &&
      clonedElement.scrollHeight > workOrderPageContentHeight;

    if (isWorkOrderLayout && !isMaterialRequestLayout) {
      const headerEl = clonedElement.querySelector(".invoice-box");
      const headerDetails = headerEl?.querySelector(".invoice-right");
      if (headerEl && headerDetails) {
        headerDetails.style.overflow = "visible";
        headerDetails.style.height = "auto";
        headerDetails.style.minHeight = "0";
        headerDetails.style.flex = "none";
        headerDetails.style.alignSelf = "stretch";
        headerEl.style.gridTemplateRows = "1fr";
        headerEl.style.alignItems = "stretch";

        const detailRows = Array.from(
          headerDetails.querySelectorAll(".purchase-detail"),
        );
        detailRows.forEach((detail) => {
          detail.style.flex = "0 0 auto";
          detail.style.minHeight = "18px";
        });

        headerDetails.style.boxSizing = "border-box";
        headerEl.style.height = "auto";
        headerEl.style.minHeight = "0";
        headerEl
          .querySelectorAll(".invoice-left, .invoice-center, .invoice-right")
          .forEach((column) => {
            column.style.height = "auto";
            column.style.alignSelf = "stretch";
            column.style.boxSizing = "border-box";
            column.style.overflow = "visible";
          });
      }

      const workOrderTable = clonedElement.querySelector(".invoice-table");
      if (workOrderTable) {
        workOrderTable.style.width = "100%";
        workOrderTable.style.maxWidth = "100%";
        workOrderTable.style.minWidth = "0";
        workOrderTable.style.tableLayout = "fixed";
      }
    }

    if (isCustomerOrderLayout || shouldRepeatWorkOrderHeader) {
      const headerSelector = ".invoice-box";
      const headerEl = clonedElement.querySelector(headerSelector);
      if (headerEl) {
        let documentRect = null;
        let headerRect = null;
        const documentCanvas = await html2pdf()
          .from(clonedElement)
          .set({
            margin: [0, 10, 0, 10],
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              logging: false,
              scrollY: 0,
              scrollX: 0,
              windowWidth: contentScrollWidth,
              onclone: (clonedDocument) => {
                const container = clonedDocument.querySelector(
                  ".html2pdf__container",
                );
                const clonedHeader = container?.querySelector(headerSelector);
                if (container && clonedHeader) {
                  documentRect = container.getBoundingClientRect();
                  headerRect = clonedHeader.getBoundingClientRect();
                }
              },
            },
          })
          .toCanvas()
          .get("canvas");

        documentRect = documentRect || clonedElement.getBoundingClientRect();
        headerRect = headerRect || headerEl.getBoundingClientRect();
        const canvasScale = documentCanvas.width / documentRect.width;
        const printableWidth = 190;
        const headerCanvas = document.createElement("canvas");
        headerCanvas.width = Math.ceil(headerRect.width * canvasScale);
        headerCanvas.height = Math.ceil(headerRect.height * canvasScale);
        headerCanvas
          .getContext("2d")
          .drawImage(
            documentCanvas,
            Math.round((headerRect.left - documentRect.left) * canvasScale),
            Math.round((headerRect.top - documentRect.top) * canvasScale),
            headerCanvas.width,
            headerCanvas.height,
            0,
            0,
            headerCanvas.width,
            headerCanvas.height,
          );

        repeatingHeaderImage = headerCanvas.toDataURL("image/png");
        repeatingHeaderOffset =
          ((headerRect.left - documentRect.left) / documentRect.width) *
          printableWidth;
        repeatingHeaderWidth =
          (headerRect.width / documentRect.width) * printableWidth;
        repeatingHeaderReserve =
          (headerCanvas.height / headerCanvas.width) * repeatingHeaderWidth -
          1.5;
        headerEl.style.display = "none";
        if (headerEl.nextElementSibling) {
          headerEl.nextElementSibling.style.marginTop = "-6px";
        }
      }
    }

    const isFinalInspectionLayout = clonedElement.classList.contains(
      "final-inspection-page-container",
    );

    const isContractReviewLayout = clonedElement.classList.contains(
      "contract-review-page",
    );

    const isRightEdgeSensitiveLayout =
      isContractReviewLayout ||
      clonedElement.classList.contains("list-of-employees-page") ||
      clonedElement.classList.contains("training-attendance-page") ||
      clonedElement.classList.contains("list-of-trainers-page") ||
      clonedElement.classList.contains("hr-employee-history-page") ||
      clonedElement.classList.contains("hr-training-needs-page") ||
      clonedElement.classList.contains("hr-induction-page") ||
      clonedElement.classList.contains("vp-voucher-page") ||
      clonedElement.classList.contains("mrm-invoice-container");

    const skipTableFontShrink =
      clonedElement.classList.contains("vp-voucher-page") ||
      clonedElement.classList.contains("hr-training-needs-page") ||
      clonedElement.classList.contains("sq-detail-layout") ||
      isProformaLayout;

    if (isProformaLayout) {
      clonedElement.style.width = "730px";
      clonedElement.style.maxWidth = "730px";
      clonedElement.style.margin = "0";
      clonedElement.style.overflow = "visible";
      clonedElement.querySelectorAll(".proforma-items").forEach((table) => {
        table.style.width = "100%";
        table.style.maxWidth = "100%";
        table.style.tableLayout = "fixed";
        table.style.borderCollapse = "separate";
        table.style.borderSpacing = "0";
      });
    }

    const forcePortrait =
      isProformaLayout ||
      (!usesRepeatingFooter &&
        (clonedElement.classList.contains("quotation-page-container") ||
          clonedElement.classList.contains("mr-page-container") ||
          clonedElement.classList.contains("jobcard-page-container") ||
          clonedElement.classList.contains(
            "incoming-inspection-page-container",
          ) ||
          clonedElement.classList.contains(
            "inprocess-inspection-page-container",
          ) ||
          clonedElement.classList.contains("purchase-enquiry-page-container") ||
          clonedElement.classList.contains(
            "purchase-quotation-page-container",
          ) ||
          clonedElement.classList.contains("purchase-order-page-container") ||
          clonedElement.classList.contains("goods-receipt-page-container")));

    if (tableWidth > 800 && !skipTableFontShrink) {
      const ths = clonedElement.querySelectorAll("th");
      const tds = clonedElement.querySelectorAll("td");

      ths.forEach((th) => {
        th.style.fontSize = "9.5px";
        th.style.padding = "6px 7px";
      });

      tds.forEach((td) => {
        td.style.fontSize = "9.5px";
        td.style.padding = "6px 7px";
      });
    }

    const exportWidth =
      clonedElement.scrollWidth ||
      clonedElement.offsetWidth ||
      contentScrollWidth;
    const tablePageBreakRepair = createPdfTablePageBreakRepair();

    // For forcePortrait layouts (e.g. Sales Enquiry / Sales Quotation):
    // always use A4 portrait with clean page breaks.
    // Content flows to additional pages when needed; no scaling.
    if (forcePortrait) {
      // Suppress horizontal scrollbar rendering only.
      // Keeping vertical overflow untouched prevents premature row pushes.
      clonedElement.querySelectorAll("*").forEach((el) => {
        const cs = window.getComputedStyle(el);
        if (cs.overflowX === "auto" || cs.overflowX === "scroll") {
          el.style.overflowX = "hidden";
        }
      });

      // Keep native table border model during export to avoid visual artifacts
      // like phantom vertical lines or unused gaps near page breaks.
      clonedElement.querySelectorAll(".invoice-table").forEach((table) => {
        const isPurchaseOrder = clonedElement.classList.contains(
          "purchase-order-page-container",
        );
        table.style.borderCollapse =
          isPurchaseOrder || isProformaLayout ? "separate" : "collapse";
        table.style.borderSpacing = "0";
        if (isPurchaseOrder) {
          const columns = Array.from(table.querySelectorAll("colgroup col"));
          const totalWidth = columns.reduce(
            (total, column) => total + parseFloat(column.style.width || "0"),
            0,
          );
          if (totalWidth > 0) {
            columns.forEach((column) => {
              column.style.width = `${(parseFloat(column.style.width) / totalWidth) * 100}%`;
            });
          }
        }
      });

      const portraitOptions = {
        margin: [8, 8, 8, 8],
        filename: pdfFileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          scrollY: 0,
          scrollX: 0,
          ...tablePageBreakRepair,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
        pagebreak: {
          mode: ["css"],
          avoid: [
            "tr",
            "thead",
            ".invoice-box",
            ".invoice-supplier-details-container",
            ".workorder-footer-grid",
            ".proforma-header",
            ".proforma-parties",
            ".proforma-lower",
            ".proforma-bank-footer",
            ...(clonedElement.classList.contains("sq-detail-layout")
              ? [
                  ".sq-grid-header",
                  ".sq-grid-middle",
                  ".sq-table thead",
                  ".sq-table tr",
                  ".sq-bottom-row",
                  ".sq-footer-area",
                  ".sq-preview-meta-footer",
                ]
              : []),
          ],
        },
      };

      try {
        const worker = html2pdf().from(clonedElement).set(portraitOptions);
        const canvas = isProformaLayout
          ? await worker.toCanvas().get("canvas")
          : null;
        const pdf = await worker.toPdf().get("pdf");

        if (canvas) {
          const [topMargin, rightMargin, bottomMargin, leftMargin] =
            portraitOptions.margin;
          const printableWidth =
            pdf.internal.pageSize.getWidth() - leftMargin - rightMargin;
          const printableHeight =
            pdf.internal.pageSize.getHeight() - topMargin - bottomMargin;
          const canvasPageHeight = Math.floor(
            (canvas.width * printableHeight) / printableWidth,
          );

          try {
            const pixels = canvas
              .getContext("2d", { willReadFrequently: true })
              .getImageData(0, 0, canvas.width, canvas.height).data;
            let lastContentRow = -1;

            for (let row = canvas.height - 1; row >= 0; row -= 1) {
              const rowStart = row * canvas.width * 4;
              for (let offset = rowStart; offset < rowStart + canvas.width * 4; offset += 4) {
                if (
                  pixels[offset + 3] > 0 &&
                  (pixels[offset] < 250 ||
                    pixels[offset + 1] < 250 ||
                    pixels[offset + 2] < 250)
                ) {
                  lastContentRow = row;
                  break;
                }
              }
              if (lastContentRow >= 0) break;
            }

            const pagesWithContent = Math.max(
              1,
              Math.ceil((lastContentRow + 1) / canvasPageHeight),
            );
            while (pdf.internal.getNumberOfPages() > pagesWithContent) {
              pdf.deletePage(pdf.internal.getNumberOfPages());
            }
          } catch (error) {
            console.warn("Unable to check proforma PDF page content:", error);
          }
        }
        drawPageNumbers(pdf);
        pdf.save(portraitOptions.filename);
      } catch (error) {
        console.error("PDF generation error:", error);
      } finally {
        document.body.removeChild(tempContainer);
        setIsDownloading(false);
      }
      return;
    }

    const genericPagebreakMode = isFinalInspectionLayout
      ? ["css", "legacy"]
      : usesRepeatingFooter
        ? ["css", "legacy"]
        : ["avoid-all", "css", "legacy"];

    const genericPagebreakAvoid = isFinalInspectionLayout
      ? [
          ".final-inspection-page-container .invoice-table thead",
          ".final-inspection-page-container .invoice-table tr",
        ]
      : usesRepeatingFooter
        ? [
            "tr",
            "thead",
            ".invoice-footer-meta-block",
            ".invoice-tax-summary",
            ".invoice-tax-summary-row",
          ]
        : ["tr", "thead", ".invoice-grid-container"];

    const contractReviewPagebreakAvoid = [
      ".contract-review-approval-block",
      ".contract-review-comments",
      ".contract-review-footer-row",
      ".contract-review-signature-row",
    ];

    const rightEdgeWidthBuffer = clonedElement.classList.contains(
      "mrm-invoice-container",
    )
      ? 56
      : isRightEdgeSensitiveLayout
        ? 28
        : 0;

    // Customer Order (and Work Order, which shares the same repeating footer)
    // always prints as portrait A4, so it must never use the wide-content
    // (landscape-oriented) canvas width/margin sizing below — mixing a wide
    // canvas with a forced portrait page is what shrank the whole document
    // down and left it stranded in the upper part of the page.
    const treatAsWide = isWideContent && !usesRepeatingFooter;

    // The Customer Order footer is redrawn separately below the true page
    // bottom margin (footerAnchorMargin). Content pagination must reserve
    // extra space above that (contentBottomMargin) so table rows/totals
    // never overlap the fixed footer and instead wrap to the next page.
    const footerAnchorMargin = treatAsWide ? [12, 8, 12, 8] : [15, 10, 10, 10];
    const customerOrderFooterReserve =
      usesRepeatingFooter && customerOrderFooterRows?.length
        ? customerOrderFooterRows.length * 8 +
          (customerOrderFooterNote ? 6 : 0) +
          4
        : 0;

    const options = {
      margin: isProformaLayout
        ? [4, 4, 4, 4]
        : isContractReviewLayout
          ? [12, 12, 12, 12]
          : usesRepeatingFooter
            ? [
                footerAnchorMargin[0] +
                  (repeatingHeaderImage ? repeatingHeaderReserve : 0),
                footerAnchorMargin[1],
                footerAnchorMargin[2] + customerOrderFooterReserve,
                footerAnchorMargin[3],
              ]
            : treatAsWide
              ? [12, 8, 12, 8]
              : [15, 10, 10, 10],
      filename: pdfFileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollY: 0,
        scrollX: 0,
        ...tablePageBreakRepair,
        width: isProformaLayout
          ? 730
          : treatAsWide
            ? exportWidth + rightEdgeWidthBuffer
            : undefined,
        windowWidth: isProformaLayout
          ? 730
          : treatAsWide
            ? exportWidth + rightEdgeWidthBuffer
            : contentScrollWidth,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        // Customer Order / Work Order always export as portrait A4: an
        // unintended landscape page here is what made the downloaded PDF
        // print off-center when the printer's paper orientation was Portrait.
        orientation:
          usesRepeatingFooter || isProformaLayout
            ? "portrait"
            : treatAsWide
              ? "landscape"
              : "portrait",
      },
      pagebreak: {
        mode: isProformaLayout
          ? ["css"]
          : isContractReviewLayout
            ? ["css", "legacy"]
            : genericPagebreakMode,
        avoid: isContractReviewLayout
          ? [...genericPagebreakAvoid, ...contractReviewPagebreakAvoid]
          : isProformaLayout
            ? [
              "tr",
              "thead",
                ".proforma-header",
                ".proforma-meta",
                ".proforma-parties",
                ".proforma-items",
                ".proforma-tax-summary",
                ".proforma-lower",
                ".proforma-bank-footer",
              ]
            : genericPagebreakAvoid,
      },
    };

    try {
      const pdf = await html2pdf()
        .from(clonedElement)
        .set(options)
        .toPdf()
        .get("pdf");
      if (
        repeatingHeaderImage ||
        (usesRepeatingFooter && customerOrderFooterRows?.length)
      ) {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let pageNum = 1; pageNum <= totalPages; pageNum += 1) {
          pdf.setPage(pageNum);
          if (repeatingHeaderImage) {
            drawRepeatingHeader(
              pdf,
              repeatingHeaderImage,
              footerAnchorMargin,
              repeatingHeaderReserve,
              repeatingHeaderOffset,
              repeatingHeaderWidth,
              isWorkOrderLayout ? [176, 183, 193] : undefined,
            );
          }
          if (usesRepeatingFooter && customerOrderFooterRows?.length) {
            drawRepeatingFooter(
              pdf,
              customerOrderFooterRows,
              footerAnchorMargin,
              customerOrderFooterNote,
              pageNum === totalPages,
            );
          }
        }
      }
      drawPageNumbers(pdf);
      pdf.save(options.filename);
    } catch (error) {
      console.error("PDF generation error:", error);
    } finally {
      document.body.removeChild(tempContainer);
      setIsDownloading(false);
    }
  };

  const handleDownload = async (selectedFormats) => {
    const needsTableRows = selectedFormats.some(
      (format) => format === "excel" || format === "pdf",
    );

    if (needsTableRows && typeof fetchAllRows === "function") {
      setIsDownloading(true);
      try {
        const allRows = await fetchAllRows();
        if (Array.isArray(allRows) && allRows.length > 0) {
          rowsOverrideRef.current = allRows;
        }
      } catch (error) {
        console.error("Failed to fetch full dataset for download:", error);
      } finally {
        setIsDownloading(false);
      }
    }

    try {
      for (const format of selectedFormats) {
        switch (format) {
          case "excel":
            if (!getExportRows().length) {
              console.error("No data available for Excel download");
              continue;
            }
            downloadExcel();
            break;
          case "pdf":
            if (!getExportRows().length) {
              console.error("No data available for PDF download");
              continue;
            }
            downloadPDF();
            break;
          case "full-page-pdf":
            await downloadFullPage();
            break;
          default:
            console.warn(`Unsupported download format: ${format}`);
        }
      }
    } finally {
      rowsOverrideRef.current = null;
    }

    setIsModalOpen(false);
  };

  const availableFormats = downloadFormats.filter((format) => {
    if ((format === "excel" || format === "pdf") && !tableData?.rows?.length) {
      return false;
    }
    if (format === "full-page-pdf" && !fullPageDownload) {
      return false;
    }
    return true;
  });

  const handleButtonClick = () => {
    if (fullPageDownload && downloadFormats.includes("full-page-pdf")) {
      downloadFullPage();
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <button
        className="download-button"
        onClick={handleButtonClick}
        disabled={isDownloading || availableFormats.length === 0}
      >
        <span className="download-button-text">
          {isDownloading ? "DOWNLOADING..." : "DOWNLOAD"}
        </span>

        <img src={Download} alt="download" className="download-button-icon" />
      </button>

      <DownloadFormatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDownload={handleDownload}
        downloadFormats={availableFormats}
      />
    </>
  );
};

export default DownloadButton;
